import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Grid,
    Card,
    CardHeader,
    CardContent,
    Typography,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Alert,
    SelectChangeEvent,
    LinearProgress,
    FormHelperText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Divider,
    Tooltip
} from '@mui/material';
import { 
    trainingManagementService, 
    Configuration, 
    Dataset, 
    TrainingRun 
} from '../../../service/trainingManagementService';
import {
    PlayArrow as PlayArrowIcon,
    Stop as StopIcon,
    StopCircle as StopCircleIcon,
    RunningWithErrors as RunningWithErrorsIcon,
    History as HistoryIcon,
    HourglassTop as HourglassTopIcon
} from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ScienceIcon from '@mui/icons-material/Science';

// Define specific model parameter types if needed, otherwise use any for now
type ModelConfigType = 'yolov8n' | 'darkyolov4_tiny';

// Helper function to get default parameters for a model
const getDefaultParams = (config: Configuration | null, modelType: ModelConfigType): any => {
    return config?.default_parameters?.[modelType] || {};
};

const TrainingControlTab: React.FC = () => {
    // Maximum width for the container
    const maxWidth = 1200;
    // Flag to check if API is active
    const apiActive = true; // This should ideally be determined dynamically
    
    // State for pagination in history table
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [trainingHistory, setTrainingHistory] = useState<any[]>([]);
    const [loading, setLoading] = useState({ trainingHistory: false });
    
    // Page change handlers
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };
    
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    
    // Define columns for history table
    const historyColumns = [
        { id: 'run_id', label: 'Run ID' },
        { id: 'model_type', label: 'Modell' },
        { id: 'dataset_name', label: 'Dataset Name' },
        { id: 'status', label: 'Status' },
        { id: 'start_time', label: 'Startzeit' },
        { id: 'end_time', label: 'Endzeit' },
        { id: 'loss', label: 'Letzter Loss' },
        { id: 'error', label: 'Fehler' }
    ];
    
    // Sorted history for the table
    const sortedTrainingHistory = useMemo(() => {
        return [...trainingHistory].sort((a, b) => 
            new Date(b.start_time || 0).getTime() - new Date(a.start_time || 0).getTime()
        );
    }, [trainingHistory]);

    const [configData, setConfigData] = useState<Configuration | null>(null);
    const [datasets, setDatasets] = useState<Dataset[]>([]);
    const [activeRuns, setActiveRuns] = useState<TrainingRun[]>([]); 
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [loadingDatasets, setLoadingDatasets] = useState(true);
    const [pollingError, setPollingError] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<{[key: string]: boolean}>({}); // e.g., start_yolov8n: true, stop_RUNID: true
    const [historyRuns, setHistoryRuns] = useState<TrainingRun[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [historyError, setHistoryError] = useState<string | null>(null);

    // Local state for UI edits, potentially initialized from defaults
    const [localParams, setLocalParams] = useState<Record<ModelConfigType, any>>({
        yolov8n: {},
        darkyolov4_tiny: {}
    });
    const [selectedDataset, setSelectedDataset] = useState<Record<ModelConfigType, string>>({
        yolov8n: '',
        darkyolov4_tiny: ''
    });

    // --- Create a map for easy dataset name lookup ---
    const datasetsMap = useMemo(() => {
        const map = new Map<string, string>();
        datasets.forEach(ds => {
            map.set(ds._id, ds.name);
        });
        return map;
    }, [datasets]);

    // Fetch global config and datasets once
    useEffect(() => {
        const loadInitialData = async () => {
            setLoadingConfig(true);
            setLoadingDatasets(true);
            try {
                const [fetchedConfig, fetchedDatasets] = await Promise.all([
                    trainingManagementService.getConfiguration(),
                    trainingManagementService.getDatasets(),
                ]);
                setConfigData(fetchedConfig);
                setDatasets(fetchedDatasets);
                // Initialize local params with defaults from config
                setLocalParams({
                    yolov8n: getDefaultParams(fetchedConfig, 'yolov8n'),
                    darkyolov4_tiny: getDefaultParams(fetchedConfig, 'darkyolov4_tiny')
                });
                 // Pre-select dataset if default is provided (optional)
                 setSelectedDataset({
                     yolov8n: fetchedConfig?.default_parameters?.yolov8n?.dataset_id || '',
                     darkyolov4_tiny: fetchedConfig?.default_parameters?.darkyolov4_tiny?.dataset_id || ''
                 });
            } catch (error) {
                console.error("Error fetching initial data:", error);
                setPollingError('Fehler beim Laden der Grundkonfiguration oder Datensätze.');
            } finally {
                setLoadingConfig(false);
                setLoadingDatasets(false);
            }
        };
        loadInitialData();
    }, []);

    // Fetch active runs periodically
    const fetchActiveRuns = useCallback(async () => {
        try {
            const fetchedActiveRuns = await trainingManagementService.getActiveTrainingRuns();
            setActiveRuns(fetchedActiveRuns);
        } catch (error) {
            console.error("Error fetching active runs:", error);
        }
    }, []);

    useEffect(() => {
        fetchActiveRuns(); // Initial fetch
        const intervalId = setInterval(fetchActiveRuns, 5000); // Poll active runs more frequently (e.g., 5s)
        return () => clearInterval(intervalId); // Cleanup interval on unmount
    }, [fetchActiveRuns]);

    // Fetch History Runs
    const loadHistoryRuns = useCallback(async (limit: number = 50, skip: number = 0) => {
        setLoadingHistory(true);
        setHistoryError(null);
        try {
            const fetchedHistoryRuns = await trainingManagementService.getTrainingRunHistory(limit, skip);
            setHistoryRuns(fetchedHistoryRuns);
        } catch (err: any) {
            console.error("Error loading training run history:", err);
            setHistoryError(`Fehler beim Laden des Trainingsverlaufs: ${err.message || 'Unbekannter Fehler'}`);
        } finally {
            setLoadingHistory(false);
        }
    }, []);

    // Load history initially
    useEffect(() => {
        loadHistoryRuns();
    }, [loadHistoryRuns]);

    // --- Event Handlers --- 
    const handleParamChange = (modelType: ModelConfigType, field: string, value: string | number) => {
        setLocalParams(prev => ({
            ...prev,
            [modelType]: { ...prev[modelType], [field]: value }
        }));
    };

    const handleDatasetChange = (modelType: ModelConfigType, event: SelectChangeEvent<string>) => {
        setSelectedDataset(prev => ({ ...prev, [modelType]: event.target.value as string }));
    };

    const handleStartTraining = async (modelType: ModelConfigType) => {
        const datasetId = selectedDataset[modelType];
        if (!datasetId) {
            setPollingError("Bitte wählen Sie zuerst einen Datensatz aus.");
            return;
        }
        setActionLoading(prev => ({ ...prev, [`start_${modelType}`]: true }));
        setPollingError(null);
        try {
            const parameters = localParams[modelType];
            await trainingManagementService.startNewTrainingRun(modelType, datasetId, parameters);
            fetchActiveRuns(); // Trigger immediate refresh
        } catch (error: any) {
            console.error(`Error starting training for ${modelType}:`, error);
            setPollingError(`Fehler beim Starten von ${modelType}: ${error.message || 'Unbekannter Fehler'}`);
        } finally {
            setActionLoading(prev => ({ ...prev, [`start_${modelType}`]: false }));
        }
    };

    const handleStopRequest = async (runId: string) => {
        setActionLoading(prev => ({ ...prev, [`stop_${runId}`]: true }));
        setPollingError(null);
        try {
             await trainingManagementService.requestStopTrainingRun(runId);
             fetchActiveRuns(); // Trigger immediate refresh
        } catch (error: any) {
            console.error(`Error requesting stop for run ${runId}:`, error);
            setPollingError(`Fehler beim Stoppen von Lauf ${runId}: ${error.message || 'Unbekannter Fehler'}`);
        } finally {
             setActionLoading(prev => ({ ...prev, [`stop_${runId}`]: false }));
        }
    };

    // --- Helper Functions --- 

    // Get progress details (MODIFIED WITH DEBUG LOGS)
    const getProgressInfo = (run: TrainingRun | undefined): { text: string; value: number } | null => {
        // 1. First Check: Is the object valid and has progress data?
        // We rely on the filter in renderActiveRunsSection for status, so we check run and progress existence.
        if (!run || !run.progress) { 
            // DEBUG: Log why the initial check failed
            console.log(`[getProgressInfo Debug] Failed initial check: run=${!!run}, progress=${run ? !!run.progress : 'N/A'}`);
            return null;
        }
        
        // DEBUG: Log the received progress object
        console.log(`[getProgressInfo Debug] Received run.progress object for run ${run._id}:`, JSON.stringify(run.progress));

        const currentLoss = run.progress.current_loss;
        const lossText = currentLoss !== null && currentLoss !== undefined 
                         ? `Loss: ${currentLoss.toFixed(4)}` 
                         : '';

        let progressText = '';
        let progressValue = 0;

        if (run.model_type?.startsWith('yolo')) { 
            const currentEpoch = run.progress.current_epoch;
            const totalEpochs = run.progress.total_epochs;
            // 2. Second Check: Are epoch data valid?
            if (currentEpoch !== null && currentEpoch !== undefined && totalEpochs && totalEpochs > 0) {
                progressValue = Math.min(100, (currentEpoch / totalEpochs) * 100);
                progressText = `Epoche ${currentEpoch} / ${totalEpochs}`;
            } else {
                 // DEBUG: Log why epoch data might be invalid
                 console.log(`[getProgressInfo Debug] Invalid epoch data for run ${run._id}: current=${currentEpoch}, total=${totalEpochs}`);
            }
        } else if (run.model_type === 'darkyolov4_tiny') {
             const currentIteration = run.progress.current_iteration;
             const totalIterations = run.progress.total_iterations;
             if (currentIteration !== null && currentIteration !== undefined && totalIterations && totalIterations > 0) {
                 progressValue = Math.min(100, (currentIteration / totalIterations) * 100);
                 progressText = `Iteration ${currentIteration} / ${totalIterations}`;
             } else {
                 console.log(`[getProgressInfo Debug] Invalid iteration data for run ${run._id}: current=${currentIteration}, total=${totalIterations}`);
             }
        }
        
        const fullText = [progressText, lossText].filter(Boolean).join(' | ');

        // DEBUG: Log the final calculated values before returning
        console.log(`[getProgressInfo Debug] Calculated values for run ${run._id}: progressValue=${progressValue}, fullText="${fullText}"`);

        // 3. Third Check: Is there anything to display?
        if (progressValue > 0 || fullText) {
            return { text: fullText || 'Fortschritt wird geladen...', value: progressValue };
        } else {
             // DEBUG: Log why null is being returned
             console.log(`[getProgressInfo Debug] Returning null because progressValue=${progressValue} and fullText="${fullText}"`);
             return null;
        }
    };

    // Determines if a specific model type training can be started
    const canStartModel = (modelType: ModelConfigType): boolean => {
        const activeCount = activeRuns.filter(run => ['running', 'pending', 'starting'].includes(run.status || '')).length;
        const modelAlreadyRunningOrPending = activeRuns.some(run => run.model_type === modelType && ['running', 'pending', 'starting', 'stopping'].includes(run.status || ''));
        const maxRuns = configData?.max_concurrent_runs ?? 0;
        return !modelAlreadyRunningOrPending && activeCount < maxRuns;
    };
    
    // Determine if a specific run can be stopped
    const canStopRun = (run: TrainingRun | undefined): boolean => {
        return !!run && ['running', 'pending', 'starting'].includes(run.status || '');
    };

    // Helper to get status chip for History Runs
    const getHistoryStatusChip = (status?: string) => {
        switch (status) {
            case 'COMPLETED':
                return <Chip label="Abgeschlossen" color="success" size="small" icon={<CheckCircleIcon />} />;
            case 'FAILED':
                return <Chip label="Fehlgeschlagen" color="error" size="small" icon={<ErrorOutlineIcon />} />;
            case 'STOPPED':
                return <Chip label="Gestoppt" color="warning" size="small" icon={<StopCircleIcon />} />;
            default:
                 if (['RUNNING', 'PENDING', 'STARTING', 'STOPPING'].includes(status || '')) {
                     return <Chip label={status} color="info" size="small" />;
                 } 
                return <Chip label={status || 'Unbekannt'} size="small" />;
        }
    };

    // --- Render Active Runs Section --- 
    const renderActiveRunsSection = () => {
        // Log the raw state fetched from the API before filtering
        console.log("[Render Active Section] Full activeRuns state before filtering:", JSON.stringify(activeRuns));

        const currentlyActiveRuns = activeRuns.filter(run => {
            const status = run.status || '';
            // Use UPPERCASE status values to match potential DB values and handle case-insensitivity implicitly if needed
            return ['RUNNING', 'PENDING', 'STARTING', 'STOPPING'].includes(status.toUpperCase());
        });
        
        // Log the result of the filtering
        console.log("[Render Active Section] Filtered currentlyActiveRuns after filtering:", JSON.stringify(currentlyActiveRuns));

        return (
            <Box>
                {currentlyActiveRuns.length === 0 ? (
                    <Typography variant="subtitle1" sx={{ textAlign: 'center', color: 'text.secondary', p: 3 }}>
                        Keine aktiven Trainingsläufe.
                    </Typography>
                ) : (
                    <Grid container spacing={2}>
                        {currentlyActiveRuns.map((run) => {
                            const progressInfo = getProgressInfo(run);
                            const stopDisabled = !canStopRun(run) || actionLoading[`stop_${run._id}`];

                            let statusIcon = <HourglassTopIcon fontSize="small" />;
                            let statusColor = 'info';
                            if (run.status === 'running') {
                                statusIcon = <RunningWithErrorsIcon fontSize="small" />;
                                statusColor = 'success';
                            } else if (run.status === 'stopping') {
                                statusIcon = <StopCircleIcon fontSize="small" />;
                                statusColor = 'warning';
                            } else if (run.status === 'pending' || run.status === 'starting') {
                                statusIcon = <HourglassTopIcon fontSize="small" />;
                                statusColor = 'info';
                            }

                            return (
                                <Grid item xs={12} sm={6} md={4} key={run._id}>
                                    <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
                                                Modell: {run.model_type}
                                            </Typography>
                                            <Typography variant="caption" display="block" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
                                                Run ID: {run._id.substring(0, 8)}... | Dataset ID: {run.dataset_id?.substring(0, 8)}...
                                            </Typography>
                                            <Chip 
                                                icon={statusIcon} 
                                                label={run.status || 'Unbekannt'} 
                                                color={statusColor as any}
                                                size="small" 
                                                sx={{ mb: 2 }} 
                                            />
                                            
                                            {progressInfo && (
                                                <Box sx={{ width: '100%', mb: 2, mt: 2 }}>
                                                    <Tooltip title={progressInfo.text} placement="top">
                                                        <LinearProgress 
                                                            variant="determinate" 
                                                            value={progressInfo.value} 
                                                            color={statusColor as any}
                                                            sx={{ height: 8, borderRadius: 5 }} 
                                                        />
                                                    </Tooltip>
                                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1, height: '2.5em', overflow: 'hidden' }}>
                                                        {progressInfo.text} 
                                                    </Typography>
                                                </Box>
                                            )}
                                            {!progressInfo && run.status !== 'stopping' && (
                                                <Box sx={{ height: '2.5em', display: 'flex', alignItems: 'center', mb: 2, mt: 2}}> 
                                                    <Typography variant="caption" display="block" color="text.secondary">Warte auf Details...</Typography>
                                                </Box>
                                            )}
                                            {run.status === 'stopping' && (
                                                <Box sx={{ height: '2.5em', display: 'flex', alignItems: 'center', mb: 2, mt: 2}}> 
                                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                        Stopp angefordert...
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            startIcon={actionLoading[`stop_${run._id}`] ? <CircularProgress size={16} color="inherit" /> : <StopIcon fontSize="small"/>}
                                            onClick={() => handleStopRequest(run._id)}
                                            disabled={stopDisabled}
                                            fullWidth
                                            sx={{ mt: 1 }}
                                        >
                                            Stopp Anfordern
                                        </Button>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Box>
        );
    };

    // --- Render Model Control Card Section ---
    const renderModelControlCard = (modelType: ModelConfigType, title: string) => {
        const canStart = canStartModel(modelType);
        const isLoading = actionLoading[`start_${modelType}`];
        const noDataset = !selectedDataset[modelType];
        const startDisabled = !canStart || isLoading || noDataset;

        // Disable inputs if cannot start
        const inputsDisabled = !canStart;

        return (
            <Grid item xs={12} sm={6} md={4} key={modelType}>
                <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
                            {title} - Starten
                        </Typography>

                        {/* More compact parameter inputs */}
                        {modelType === 'yolov8n' && (
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Epochen"
                                        type="number"
                                        size="small"
                                        value={localParams.yolov8n?.epochs ?? ''}
                                        onChange={(e) => handleParamChange('yolov8n', 'epochs', parseInt(e.target.value) || 0)}
                                        disabled={inputsDisabled}
                                        fullWidth
                                        margin="dense"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Batch Größe"
                                        type="number"
                                        size="small"
                                        value={localParams.yolov8n?.batch_size ?? ''}
                                        onChange={(e) => handleParamChange('yolov8n', 'batch_size', parseInt(e.target.value) || 0)}
                                        disabled={inputsDisabled}
                                        fullWidth
                                        margin="dense"
                                    />
                                </Grid>
                            </Grid>    
                        )}
                        {modelType === 'darkyolov4_tiny' && (
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Max. Iterationen"
                                        type="number"
                                        size="small"
                                        value={localParams.darkyolov4_tiny?.max_iterations ?? ''}
                                        onChange={(e) => handleParamChange('darkyolov4_tiny', 'max_iterations', parseInt(e.target.value) || 0)}
                                        disabled={inputsDisabled}
                                        fullWidth
                                        margin="dense"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Batch Größe"
                                        type="number"
                                        size="small"
                                        value={localParams.darkyolov4_tiny?.batch_size ?? ''}
                                        onChange={(e) => handleParamChange('darkyolov4_tiny', 'batch_size', parseInt(e.target.value) || 0)}
                                        disabled={inputsDisabled}
                                        fullWidth
                                        margin="dense"
                                    />
                                </Grid>
                            </Grid>
                        )}
                        
                        {/* Dataset Selector */ }
                        <FormControl fullWidth size="small" disabled={inputsDisabled} sx={{ mb: 2 }}>
                            <InputLabel id={`${modelType}-dataset-label`}>Datensatz</InputLabel>
                            <Select
                                labelId={`${modelType}-dataset-label`}
                                label="Datensatz"
                                value={selectedDataset[modelType] || ''}
                                onChange={(e) => handleDatasetChange(modelType, e)}
                            >
                                <MenuItem value=""><em>Keiner ausgewählt</em></MenuItem>
                                {datasets.map(ds => (
                                    <MenuItem key={ds._id} value={ds._id}>{ds.name}</MenuItem>
                                ))}
                            </Select>
                            {!selectedDataset[modelType] && canStart && (
                                <FormHelperText error>Bitte Datensatz auswählen.</FormHelperText>
                            )}
                        </FormControl>
                    </Box>
                    
                    {/* Start Button */}
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={actionLoading[`start_${modelType}`] ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                        onClick={() => handleStartTraining(modelType)}
                        disabled={startDisabled}
                        fullWidth
                        size="small"
                        sx={{ mt: 1 }}
                    >
                        Training Starten
                    </Button>
                </Paper>
            </Grid>
        );
    };

    // --- Main Render --- 
    return (
        <Box sx={{ p: 3 }}>
            {pollingError && <Alert severity="error" sx={{ mb: 3 }}>{pollingError}</Alert>}

            {/* Start New Training Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" gutterBottom component="div" sx={{ mb: 3, pt: 2, borderTop: '1px solid #eee' }}>
                    Neues Training starten
                </Typography>
                {(loadingConfig || loadingDatasets) ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
                ) : (
                <Grid container spacing={2}>
                    {configData?.supported_models && configData.supported_models.length > 0 ? (
                        configData.supported_models.map((model) => {
                            if (model === 'yolov8n' || model === 'darkyolov4_tiny') {
                                const title = model === 'yolov8n' ? 'YOLOv8' : 'Darkyolov4-tiny';
                                return renderModelControlCard(model as ModelConfigType, title);
                            } else {
                                console.warn(`[TrainingControlTab] Unsupported model type found in config: ${model}. Card not rendered.`);
                                return null;
                            }
                        })
                    ) : (
                        <Grid item xs={12}>
                            <Alert severity="warning">Keine unterstützten Modelle in der Konfiguration gefunden.</Alert>
                        </Grid>
                    )}
                </Grid>
                )}
            </Box>

            {/* Active Runs Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" gutterBottom component="div" sx={{ display: 'flex', alignItems: 'center', mb: 3, pt: 2, borderTop: '1px solid #eee' }}>
                   <RunningWithErrorsIcon sx={{ mr: 1 }} /> Aktive Trainingsläufe
                </Typography>
                {renderActiveRunsSection()}
            </Box>

            {/* Training History Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" gutterBottom component="div" sx={{ display: 'flex', alignItems: 'center', mb: 3, pt: 2, borderTop: '1px solid #eee' }}>
                    <HistoryIcon sx={{ mr: 1 }} /> Trainingsverlauf
                </Typography>
                {historyError && <Alert severity="error" sx={{ mb: 3 }}>{historyError}</Alert>}
                <TableContainer component={Paper} sx={{ p: 2 }}>
                    <Table sx={{ minWidth: 650 }} aria-label="training history table" size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Run ID</TableCell>
                                <TableCell>Modell</TableCell>
                                <TableCell>Dataset Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Startzeit</TableCell>
                                <TableCell>Endzeit</TableCell>
                                <TableCell>Letzter Loss</TableCell>
                                <TableCell>Fehler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loadingHistory ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : historyRuns.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">Kein Verlauf gefunden.</TableCell>
                                </TableRow>
                            ) : (
                                historyRuns.map((run) => (
                                    <TableRow
                                        key={run._id}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {run._id?.substring(0, 8) ?? 'N/A'}...
                                        </TableCell>
                                        <TableCell>{run.model_type}</TableCell>
                                        <TableCell>
                                            {run.dataset_id ? (datasetsMap.get(run.dataset_id) ?? run.dataset_id.substring(0,8)) : 'N/A'}
                                        </TableCell>
                                        <TableCell>{getHistoryStatusChip(run.status)}</TableCell>
                                        <TableCell>{run.start_time ? new Date(run.start_time).toLocaleString() : 'N/A'}</TableCell>
                                        <TableCell>{run.end_time ? new Date(run.end_time).toLocaleString() : 'N/A'}</TableCell>
                                        <TableCell>
                                            {typeof run.progress?.current_loss === 'number' 
                                                ? run.progress.current_loss.toFixed(4) 
                                                : 'N/A'}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {run.error_message || '-'}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

export default TrainingControlTab; 